import { useLayoutEffect, useState } from 'react';
import type { RefObject } from 'react';

/**
 * Measures the longest rendered line of the element's text content via
 * Range.getClientRects() so the element can be given an explicit width equal
 * to its longest line — letting auto margins center the text block
 * horizontally without center-aligning the lines themselves.
 *
 * Re-measures whenever `text` or `remeasureKey` changes and on parent resize. Guarded for
 * jsdom's missing Range.getClientRects.
 *
 * Returns the longest line width in CSS pixels (rounded up), or `undefined`
 * until a successful measurement.
 */
export function useLongestLineWidth(
  ref: RefObject<HTMLElement | null>,
  text: string,
  remeasureKey?: number,
): number | undefined {
  const [lineWidth, setLineWidth] = useState<number | undefined>(undefined);

  useLayoutEffect(() => {
    const node = ref.current;
    if (!node) return;

    const measure = (): void => {
      const previousWidth = node.style.width;
      node.style.width = '';
      const range = document.createRange();
      range.selectNodeContents(node);
      if (typeof range.getClientRects !== 'function') {
        range.detach();
        if (previousWidth) node.style.width = previousWidth;
        return;
      }
      const rects = range.getClientRects();
      let max = 0;
      for (const rect of rects) {
        if (rect.width > max) max = rect.width;
      }
      range.detach();
      if (previousWidth) node.style.width = previousWidth;
      if (max > 0) setLineWidth(Math.ceil(max));
    };

    measure();

    const parent = node.parentElement;
    if (!parent || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(parent);
    return () => observer.disconnect();
  }, [ref, text, remeasureKey]);

  return lineWidth;
}
